/* Application state.
 *
 * Slices are kept narrow and updates are immutable so a burst of market data
 * re-renders only the components that actually read the changed instrument.
 * A single monolithic store would re-render the whole terminal on every tick.
 */
import { create } from 'zustand'
import type {
  ConnectionHealth, Depth, Execution, Order, Position, Quote,
  StrategyStatus, TradePrint, TradingMode,
} from '@/types'

interface QuoteSlice {
  quotes: Map<string, Quote>
  applyQuotes: (batch: Map<string, Quote>) => void
}

interface SessionSlice {
  authenticated: boolean
  userId: string | null
  roles: string[]
  permissions: string[]
  tradingMode: TradingMode
  killSwitchEngaged: boolean
  setSession: (s: { userId: string; roles: string[]; permissions: string[] }) => void
  clearSession: () => void
  setTradingMode: (m: TradingMode) => void
  setKillSwitch: (engaged: boolean) => void
  can: (permission: string) => boolean
}

interface BlotterSlice {
  orders: Order[]
  positions: Position[]
  executions: Execution[]
  strategies: StrategyStatus[]
  /** Trade prints, newest first, per selected instrument. */
  timeAndSales: TradePrint[]
  depth: Map<string, Depth>
  setOrders: (o: Order[]) => void
  setPositions: (p: Position[]) => void
  setExecutions: (e: Execution[]) => void
  setStrategies: (s: StrategyStatus[]) => void
  addTradePrint: (p: TradePrint) => void
  setDepth: (instrument: string, d: Depth) => void
}

interface UiSlice {
  theme: 'dark' | 'light'
  selectedInstrument: string | null
  commandPaletteOpen: boolean
  activeBlotter: string
  toggleTheme: () => void
  /** Adopt a theme without toggling — used to sync the store with what
   *  initTheme() already applied to <html> before React mounted. */
  setTheme: (theme: 'dark' | 'light') => void
  selectInstrument: (id: string | null) => void
  setCommandPalette: (open: boolean) => void
  setActiveBlotter: (tab: string) => void
}

interface HealthSlice {
  health: ConnectionHealth
  setHealth: (h: Partial<ConnectionHealth>) => void
}

type Store = QuoteSlice & SessionSlice & BlotterSlice & UiSlice & HealthSlice

export const useStore = create<Store>((set, get) => ({
  // --- quotes ---
  quotes: new Map(),
  applyQuotes: (batch) =>
    set((state) => {
      // Copy once per FLUSH, not once per tick. The feed layer has already
      // conflated and frame-gated, so this runs at most once per frame.
      const next = new Map(state.quotes)
      for (const [k, v] of batch) next.set(k, v)
      return { quotes: next }
    }),

  // --- session ---
  authenticated: false,
  userId: null,
  roles: [],
  permissions: [],
  tradingMode: 'PAPER',
  killSwitchEngaged: false,
  setSession: ({ userId, roles, permissions }) =>
    set({ authenticated: true, userId, roles, permissions }),
  clearSession: () =>
    set({ authenticated: false, userId: null, roles: [], permissions: [] }),
  setTradingMode: (tradingMode) => set({ tradingMode }),
  setKillSwitch: (killSwitchEngaged) => set({ killSwitchEngaged }),
  // The UI hides what a user cannot do, but the API enforces it. Hiding a
  // control is a courtesy, never the security boundary.
  can: (permission) => get().permissions.includes(permission),

  // --- blotters ---
  orders: [],
  positions: [],
  executions: [],
  strategies: [],
  timeAndSales: [],
  depth: new Map(),
  setOrders: (orders) => set({ orders }),
  setPositions: (positions) => set({ positions }),
  setExecutions: (executions) => set({ executions }),
  setStrategies: (strategies) => set({ strategies }),
  addTradePrint: (p) =>
    set((s) => {
      // Bounded ring. An unbounded tape grows without limit on a busy
      // instrument and eventually stalls the browser.
      const next = [p, ...s.timeAndSales]
      return { timeAndSales: next.length > 500 ? next.slice(0, 500) : next }
    }),
  setDepth: (instrument, d) =>
    set((s) => {
      const next = new Map(s.depth)
      next.set(instrument, d)
      return { depth: next }
    }),

  // --- ui ---
  theme: 'dark',
  selectedInstrument: null,
  commandPaletteOpen: false,
  activeBlotter: 'positions',
  toggleTheme: () =>
    set((s) => {
      const theme = s.theme === 'dark' ? 'light' : 'dark'
      document.documentElement.setAttribute('data-theme', theme)
      try {
        localStorage.setItem('qorvex.theme', theme)
      } catch {
        /* private browsing or blocked storage: the theme just does not persist */
      }
      return { theme }
    }),
  setTheme: (theme) => {
    document.documentElement.setAttribute('data-theme', theme)
    try { localStorage.setItem('qorvex.theme', theme) } catch { /* non-fatal */ }
    set({ theme })
  },
  selectInstrument: (selectedInstrument) => set({ selectedInstrument }),
  setCommandPalette: (commandPaletteOpen) => set({ commandPaletteOpen }),
  setActiveBlotter: (activeBlotter) => set({ activeBlotter }),

  // --- health ---
  health: {
    api: 'disconnected',
    engine: 'unavailable',
    marketData: 'disconnected',
    lastUpdate: 0,
  },
  setHealth: (h) => set((s) => ({ health: { ...s.health, ...h } })),
}))

/** Selector for one instrument. Components using this re-render only when
 *  THEIR instrument changes, not on every batch. */
export const useQuote = (instrumentId: string | null) =>
  useStore((s) => (instrumentId ? s.quotes.get(instrumentId) : undefined))
